import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  Paper,
  rem,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconExclamationMark, IconLock, IconMail } from "@tabler/icons-react";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckBrandInviteCodeQuery,
  CheckBrandInviteCodeQueryVariables,
  CheckForExistingUserQuery,
  CheckForExistingUserQueryVariables,
  SignUpNewUserMutation,
  SignUpNewUserMutationVariables,
} from "../../../../gql/__generated__/graphql";
import { CHECK_BRAND_INVITE_CODE } from "../../../apis/lambda/GraphQL/Brand/queries";
import { SIGN_UP_NEW_USER } from "../../../apis/lambda/GraphQL/User/mutations";
import { CHECK_FOR_EXISTING_USER } from "../../../apis/lambda/GraphQL/User/queries";
import { userInfoAtom } from "../../../atoms";
import { getLinearGradient } from "../../../mantineTheme";
import {
  isLoggedIn as isLoggedInFunction,
  saveJWTCookie,
} from "../../../utils/jwtUtils";

interface SignupFormValues {
  email: string;
  password: string;
  acceptedTerms: boolean;
  brandInviteCode?: string;
}

function SignupPage() {
  const navigate = useNavigate();

  // React State
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [userType, setUserType] = useState<"creator" | "brand">("creator");

  // Jotai State
  const setUserInfo = useSetAtom(userInfoAtom);

  // Form handling
  const form = useForm<SignupFormValues>({
    initialValues: {
      email: "",
      password: "",
      acceptedTerms: false,
      brandInviteCode: "",
    },
    validate: {
      email: (value) => {
        if (!value) return "Email is required";
        if (!/\S+@\S+\.\S+/.test(value)) return "Email is invalid";
        return null;
      },
      password: (value) => {
        if (isSigningUp && !value) return "Password is required";
        if (isSigningUp && value.length < 6)
          return "Password must be at least 6 characters";
        return null;
      },
      acceptedTerms: (value) => {
        if (isSigningUp && !value)
          return "You must accept the Terms of Service and Privacy Policy";
        return null;
      },
      brandInviteCode: (value) => {
        if (isSigningUp && userType === "brand" && !value)
          return "Brand invite code is required";
        return null;
      },
    },
  });

  // GraphQL Queries
  const [checkExistingUser, { loading }] = useLazyQuery<
    CheckForExistingUserQuery,
    CheckForExistingUserQueryVariables
  >(CHECK_FOR_EXISTING_USER);

  const [checkBrandInviteCode, { loading: loadingInviteCode }] = useLazyQuery<
    CheckBrandInviteCodeQuery,
    CheckBrandInviteCodeQueryVariables
  >(CHECK_BRAND_INVITE_CODE);

  // GraphQL Mutations
  const [signUpNewUser, { loading: loadingSignUp }] = useMutation<
    SignUpNewUserMutation,
    SignUpNewUserMutationVariables
  >(SIGN_UP_NEW_USER);

  useEffect(() => {
    if (isLoggedInFunction()) {
      navigate("/");
    }
  }, [navigate]);

  async function handleCheckEmail(values: SignupFormValues) {
    try {
      // Check if email is already registered
      const emailResult = await checkExistingUser({
        variables: {
          email: values.email,
        },
      });

      const response = emailResult.data?.checkForExistingUser;
      if (response?.exists && !response?.validated) {
        notifications.show({
          title: "Email already registered",
          message:
            "This email is already registered. Please log in or use a different email.",
          color: "yellow",
          icon: <IconExclamationMark size={16} />,
        });
        return;
      }

      // If brand user, also validate invite code
      if (userType === "brand") {
        if (!values.brandInviteCode) {
          notifications.show({
            title: "Invite code required",
            message: "Please enter your brand invite code to continue.",
            color: "yellow",
            icon: <IconExclamationMark size={16} />,
          });
          return;
        }

        const inviteCodeResult = await checkBrandInviteCode({
          variables: {
            code: values.brandInviteCode,
          },
        });

        if (!inviteCodeResult.data?.checkBrandInviteCode) {
          notifications.show({
            title: "Invalid invite code",
            message: "The brand invite code you entered is invalid. Please check and try again.",
            color: "red",
            icon: <IconExclamationMark size={16} />,
          });
          return;
        }
      }

      // All validations passed, proceed to password step
      setIsSigningUp(true);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "An error occurred during validation. Please try again.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    }
  }

  async function handleSignup(values: SignupFormValues) {
    try {
      const actualUserType = userType === "brand" ? "BRAND_USER" : "CREATOR";

      const response = await signUpNewUser({
        variables: {
          input: {
            email: values.email,
            password: values.password,
            userType: actualUserType,
            brandInviteCode: userType === "brand" ? values.brandInviteCode : undefined,
          },
        },
      });

      if (response.data) {
        const jwt = response.data.signUpNewUser;
        saveJWTCookie(jwt);
        setUserInfo({
          email: values.email,
          userType: actualUserType,
        });
        navigate("/signup/complete", {
          state: {
            email: values.email,
          },
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Signup failed",
        message: error?.message || "An error occurred during signup. Please try again.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    }
  }

  const loadingSomething = loading || loadingInviteCode || loadingSignUp;

  return (
    <Box
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: rem(16),
      }}
    >
      <Container size="xs" w="100%">
        <Stack gap="xl" align="stretch" w="100%">
          <Title
            order={1}
            ta="center"
            style={{
              // background:
              //   "linear-gradient(45deg, var(--mantine-color-tertiary-6), var(--mantine-color-yellow-6))",
              background: getLinearGradient("primary"),
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.3,
            }}
          >
            Sign Up
          </Title>

          <Paper p="xl" radius="lg" shadow="md" withBorder>
            <Stack gap="lg">
              <SegmentedControl
                value={userType}
                onChange={(value) => setUserType(value as "creator" | "brand")}
                data={[
                  { label: "Creator", value: "creator" },
                  { label: "Brand", value: "brand" },
                ]}
                fullWidth
                color="secondary"
              />

              <form
                onSubmit={form.onSubmit(
                  isSigningUp ? handleSignup : handleCheckEmail
                )}
              >
                  <Stack gap="lg">
                    <TextInput
                      label="Email"
                      placeholder="Enter your email"
                      leftSection={<IconMail size={16} />}
                      disabled={isSigningUp}
                      {...form.getInputProps("email")}
                    />

                    {userType === "brand" && (
                      <TextInput
                        label="Brand Invite Code"
                        placeholder="Enter your brand invite code"
                        description="You should have received this code during onboarding"
                        disabled={isSigningUp}
                        {...form.getInputProps("brandInviteCode")}
                      />
                    )}

                    {isSigningUp && !loadingSomething && (
                      <>
                        <TextInput
                          label="Password"
                          type="password"
                          placeholder="Create a password"
                          leftSection={<IconLock size={16} />}
                          {...form.getInputProps("password")}
                        />

                        <Checkbox
                          label={
                            <Text size="sm">
                              I agree to the{" "}
                              <Anchor
                                component={Link}
                                to="/terms-of-service"
                                c="secondary"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Terms of Service
                              </Anchor>{" "}
                              and{" "}
                              <Anchor
                                component={Link}
                                to="/privacy-policy"
                                c="secondary"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Privacy Policy
                              </Anchor>
                            </Text>
                          }
                          {...form.getInputProps("acceptedTerms", {
                            type: "checkbox",
                          })}
                        />
                      </>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      fullWidth
                      loading={loadingSomething}
                      leftSection={
                        loadingSomething ? <Loader size={16} /> : undefined
                      }
                    >
                      Sign Up
                    </Button>
                  </Stack>
                </form>
            </Stack>
          </Paper>

          <Group justify="center">
            <Text size="sm" c="gray.6">
              Already have an account?{" "}
              <Anchor component={Link} to="/login" c="secondary" fw={600}>
                Log In
              </Anchor>
            </Text>
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}

export default SignupPage;
